import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ClonedUser, SecurityGroup, SecurityGroupDetail, UserAccount } from 'src/app/admin/models';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { emptyPagingToken, FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { IntegrationLookupTableRequestEnum } from 'src/app/shared/models/enums/integration-lookup-table.enum';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { interpolateParams } from 'src/app/shared/utilities/interpolate-params';
import { appendQuery, arrayToQuery, combine, formatDate } from 'src/app/shared/utilities/route';
import { TicketEmailSettings } from 'src/app/travelx/models/email-settings.model';
import { OptionModel } from '../shared/grid/filters/option.model';
import { PathfindersFlags } from '../shared/models/enums/team-feature-flags/pathfinders-feature-flags.enum';
import { LDFeatureManager } from '../shared/services/ld-feature-manager';
import {
    ComplianceMappingImportHistoryModel,
    ContractIdsStartAndEndUpdateRequestV2,
    FacilityExpenseCapsRequest,
    FacilityExpenseCapsResponse,
    RequirementMappingRequest,
    ResponseErrors,
    TravelContractsContractsImportRequest,
    TravelContractsCSVResults,
    UpdateContractIdsResultV2,
    UserModel
} from './models';
import { UserNameChange } from './models/userNameChange.model';

@Injectable({
    providedIn: 'root'
})
export class AdminService {
    currentUserType$: Observable<string>;

    private readonly _userTypeSource$ = new BehaviorSubject('External');
    private readonly _coreApiPath: string;

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _toasterService: ToasterService,
        private readonly _featureManager: LDFeatureManager
    ) {
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'api');
        this.currentUserType$ = this._userTypeSource$.asObservable();
    }

    changeUserType(userType: string): void {
        this._userTypeSource$.next(userType);
    }

    getProfiles(userId: string, searchText: string): Observable<OptionModel[]> {
        const endpointUrl = `${this._settings.coreAPIUrl}ayanova/account/linked-profiles/search/${userId}?searchString=${searchText}`;
        return this._httpClient.get<OptionModel[]>(endpointUrl);
    }

    getUsers(args: FetchArgs): Observable<PagingToken<UserModel[]>> {
        let url = `${this._coreApiPath}/account/userswithpaging`;

        if (args.filter) {
            for (const prop in args.filter) {
                if (Object.prototype.hasOwnProperty.call(args.filter, prop)) {
                    const val = args.filter[prop];

                    if (Array.isArray(val)) {
                        url = appendQuery(url, arrayToQuery(prop, val));
                    } else if (val) {
                        url = appendQuery(url, `${prop}=${val}`);
                    }
                }
            }
        }

        if (args.sort && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        return this.getPaged(appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`));
    }

    getSecurityGroups(args: FetchArgs): Observable<PagingToken<SecurityGroup[]>> {
        return this._featureManager.isEnabled(PathfindersFlags.SecurityGroupsAudit).pipe(
            switchMap((isEnabled) => {
                const url = isEnabled
                    ? `${this._coreApiPath}/securitygroup/search`
                    : `${this._coreApiPath}/securitygroup`;
                return this.postPaged<SecurityGroup>(url, args);
            })
        );
    }

    getSecurityGroupsForUser(userId: string): Observable<SecurityGroupDetail[]> {
        const url = `${this._coreApiPath}/account/users/${userId}/security-groups`;
        return this._httpClient.get<SecurityGroupDetail[]>(url);
    }

    getUser(id: string): Observable<UserModel> {
        return this._httpClient.get<UserModel>(`${this._coreApiPath}/account/users/details/${id}`);
    }

    getRoles() {
        return this._httpClient.get<any>(`${this._coreApiPath}/account/roles`);
    }

    getPermissions() {
        return this._httpClient.get<any>(`${this._coreApiPath}/account/permissions`);
    }

    updateUserName(data: UserNameChange) {
        const url = `${this._settings.coreAPIUrl}ayanova/account/internal-users/${data.id}`;
        return this._httpClient.put<any>(url, data);
    }

    unblockUser(userId: string) {
        const url = `${this._settings.coreAPIUrl}/auth-user/${userId}/unblock`;
        return this._httpClient.post<any>(url, null);
    }

    editExternalUser(userId: string, profile: UserModel) {
        const url = `${this._settings.coreAPIUrl}ayanova/account/${userId}`;
        return this._httpClient.put<any>(url, profile);
    }

    activateOrDeactivateUser(id: number, data: any) {
        const url = `${this._settings.coreAPIUrl}ayanova/account/internal-users/${id}/status`;
        return this._httpClient.put<any>(url, data);
    }

    getEmailSettings(): Observable<TicketEmailSettings[]> {
        const url = `${this._coreApiPath}/EmailNotificationSettings/GetSettings`;
        return this._httpClient.get<TicketEmailSettings[]>(url);
    }

    getUsersByName(name: string, companyId: number): Observable<UserAccount[]> {
        let url = `${this._coreApiPath}/account/users/search/${name}`;
        if (companyId) {
            url += `?companyId=${companyId}`;
        }
        return this._httpClient.get<UserAccount[]>(url);
    }

    userHasCloneRestrictedPermission(id: number): Observable<boolean> {
        const url = `${this._coreApiPath}/account/users/${id}/clone-restricted`;
        return this._httpClient.get<boolean>(url);
    }

    createClone(cloneUser: unknown): Observable<ClonedUser> {
        const url = `${this._settings.coreAPIUrl}ayanova/account/create`;
        return this._httpClient.post<ClonedUser>(url, cloneUser);
    }

    saveEmailSettings(data: TicketEmailSettings[]): Observable<unknown> {
        const url = `${this._coreApiPath}/EmailNotificationSettings/SaveSettings`;
        return this._httpClient.put(url, data);
    }

    timeCardCreate(data): Observable<unknown> {
        const requestDate = formatDate(data.timeCardDate);
        let url;

        if (data.viewTypeId) {
            url = `${this._settings.coreAPIUrl}background/timecard/batch/perdiem`;
        } else {
            url = `${this._settings.coreAPIUrl}background/timecard/batch/contract`;
        }

        return this._httpClient.post(url, { requestDate: requestDate, isInline: true });
    }

    uploadIntegrationLookupsTable(file: File, table: string): Observable<ResponseErrors> {
        const url = `${this._settings.coreAPIUrl}/ayanova/${IntegrationLookupTableRequestEnum[table]}`;
        const formData = new FormData();
        formData.append('file', file);
        return this._httpClient.post<any>(url, formData);
    }

    getFileUploader(): FileUploader {
        const options: any = {
            url: `${this._settings.coreAPIUrl}ayanova/candidate/contracts/uploadCSVContractsV2`,
            isHTML5: true,
            autoUpload: false,

            headers: [],
            removeAfterUpload: true,
            itemAlias: 'file'
        };

        const fileUploader: FileUploader = new FileUploader(options);

        return fileUploader;
    }

    getComplianceRequirementMappingFileUploader(): FileUploader {
        const options: any = {
            url: `${this._settings.coreAPIUrl}ayanova/admin/compliance-requirement-mappings/validate`,
            isHTML5: true,
            autoUpload: false,

            headers: [],
            removeAfterUpload: true,
            itemAlias: 'file'
        };

        const fileUploader: FileUploader = new FileUploader(options);

        return fileUploader;
    }

    getExpenseCapsFileUploader(): FileUploader {
        const options: any = {
            url: `${this._settings.coreAPIUrl}AyaNova/admin/facility-expense-caps/validate`,
            isHTML5: true,
            autoUpload: false,
            headers: [],
            removeAfterUpload: true,
            itemAlias: 'file'
        };

        const fileUploader: FileUploader = new FileUploader(options);

        return fileUploader;
    }

    setImportedRowsNumber(data: any): number {
        if (!data.errors.length) {
            return data.rows;
        } else {
            const grouppedErrors = {};
            data.errors.forEach((item) => {
                if (!grouppedErrors[item.row]) {
                    grouppedErrors[item.row] = [];
                }
                grouppedErrors[item.row].push(item);
            });

            return data.rows - Object.keys(grouppedErrors).length;
        }
    }

    importCSVIntoSystem(data: TravelContractsContractsImportRequest): Observable<TravelContractsCSVResults[]> {
        const url = `${this._settings.coreAPIUrl}ayanova/candidate/contracts/processContractsForStartAndEndDates`;
        return this._httpClient.post<TravelContractsCSVResults[]>(url, data);
    }

    importCSVIntoSystemV2(data: ContractIdsStartAndEndUpdateRequestV2): Observable<UpdateContractIdsResultV2[]> {
        const url = `${this._settings.coreAPIUrl}ayanova/candidate/contracts/processContractsForStartAndEndDatesV2`;
        return this._httpClient.post<UpdateContractIdsResultV2[]>(url, data);
    }

    saveMappings(data: RequirementMappingRequest): Observable<number> {
        const url = `${this._settings.coreAPIUrl}ayanova/admin/compliance-requirement-mappings/save`;
        return this._httpClient.post<number>(url, data);
    }

    saveExpenseCapConfigurations(data: FacilityExpenseCapsRequest): Observable<FacilityExpenseCapsResponse> {
        const url = `${this._settings.coreAPIUrl}AyaNova/admin/facility-expense-caps/save`;
        return this._httpClient.post<FacilityExpenseCapsResponse>(url, data);
    }

    downloadAdminTemplate(): Observable<any> {
        const url = `${this._settings.coreAPIUrl}ayanova/candidate/contracts/admin-template`;
        return this._httpClient.get<any>(url, { responseType: 'blob' as 'json' }).pipe(
            map((response) => {
                const dataType = response.type;

                const binaryData = [];
                binaryData.push(response);

                return new Blob(binaryData, { type: dataType });
            })
        );
    }

    downloadComplianceRequirementAdminTemplate(): Observable<any> {
        const url = `${this._settings.coreAPIUrl}ayanova/admin/compliance-requirement-mappings/template`;
        return this._httpClient.get(url, { responseType: 'blob' }).pipe(
            map((response) => {
                const dataType = response.type;

                const binaryData = [];
                binaryData.push(response);

                return new Blob(binaryData, { type: dataType });
            })
        );
    }

    getCacheContainers(): Observable<string[]> {
        return this._httpClient.get<string[]>(`${this._settings.coreAPIUrl}cache`);
    }

    clearCacheContainer(containerName: string): Observable<any> {
        return this._httpClient.delete<any>(`${this._settings.coreAPIUrl}cache?cacheObjectType=${containerName}`);
    }

    protected getPaged<T>(url: string): Observable<PagingToken<T[]>> {
        return this._httpClient.get<PagingToken<T[]>>(url).pipe(
            catchError((errorResult: unknown) => {
                if (errorResult instanceof HttpErrorResponse && errorResult.status === 403) {
                    this._toasterService.toast(errorResult.message);
                }
                return of(emptyPagingToken<T>());
            })
        );
    }

    protected postPaged<T>(url: string, body: any | null): Observable<PagingToken<T[]>> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        };

        return this._httpClient.post<PagingToken<T[]>>(url, body, httpOptions).pipe(
            catchError((errorResult: unknown) => {
                if (errorResult instanceof HttpErrorResponse && errorResult.status === 403) {
                    this._toasterService.toast(errorResult.message);
                }
                return of(emptyPagingToken<T>());
            })
        );
    }

    removeSpecialCharactersFromBodyObject(jsonString: string) {
        return jsonString.replace('&', '').replace('?', '').replace('#', '');
    }

    getComplianceMappingImportHistory(): Observable<ComplianceMappingImportHistoryModel[]> {
        return this._httpClient.get<ComplianceMappingImportHistoryModel[]>(
            `${this._settings.coreAPIUrl}ayanova/admin/compliance-requirement-mappings/import-history`
        );
    }

    getRegeneratedSignedBackgroundPdfUrl(contractId: number, timezone: string): string {
        const url = `${this._settings.coreAPIUrl}ayanova/admin/travel-contracts/${contractId}/regenerate-signed-contract-background-document`;
        const params = interpolateParams({ timezone: timezone });
        return params.keys().length ? `${url}?${params.toString()}` : url;
    }
}
